/**
 * The LeapFILE secure-download card the pane inserts into the compose body on
 * Send, plus the helpers to find an existing one (so a re-send replaces it rather
 * than stacking a second link).
 *
 * The card lands in arbitrary email clients (incl. Outlook desktop's Word engine),
 * so the markup is bulletproof: table-based layout, all styling inline, a VML
 * roundrect fallback for the filled button, and no flexbox.
 *
 * Each block carries a per-transfer `id` (``lf-secure-link-<transferOid>``) so the
 * pane can find ONLY the block it inserted this session — never a block for a
 * different transfer that may be sitting in quoted reply/forward history. Outlook
 * web's compose sanitizer strips custom attrs, so detection keys off the `id`
 * (tolerating an `x_` prefix Outlook may inject) and a depth-counting <div> walk,
 * not a non-greedy regex.
 *
 * This module owns everything about the card in one place:
 *   - build its HTML       — buildSecureLinkBlock / buildManifest (pure)
 *   - find it in a string  — findSecureLinkRanges / findSecureLinkOids (pure)
 *   - put it in/out of the — insertSecureLink / refreshSecureLink /
 *     live email (Office.js)  discardOwnedTransfer (the bottom section)
 * The render/parse helpers have no side effects; the body ops at the bottom are the
 * only place the pane writes to the compose body.
 */

import { getBodyHtml, setBodyHtml, setSelectedHtml } from "../../js/utils/compose_item.js";
import { deleteTransfer } from "../../js/utils/api.js";
import { state } from "./files_state.js";

// Base id; the live block's wrapper is `${SECURE_LINK_ID}-<transferOid>` so blocks
// for different transfers are distinguishable and ops never cross transfers.
const SECURE_LINK_ID = "lf-secure-link";

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// The wrapper div for the block of ONE specific transfer OID (tolerating the x_
// prefix Outlook web may inject). Scoped to the OID so quoted blocks for other
// transfers are never matched.
function secureLinkOpenRegex(transferOid) {
  return new RegExp(
    `<div\\b[^>]*\\bid\\s*=\\s*["'](?:x_)?${escapeRegExp(SECURE_LINK_ID)}-${escapeRegExp(transferOid)}["'][^>]*>`,
    "i",
  );
}

// Depth-counting <div> walk from the first match of openRegex at/after startFrom.
function findRange(html, openRegex, startFrom = 0) {
  const open = openRegex.exec(html.slice(startFrom));
  if (!open) return null;
  const start = startFrom + open.index;
  let depth = 1;
  let pos = start + open[0].length;
  while (pos < html.length && depth > 0) {
    const nextOpen = html.indexOf("<div", pos);
    const nextClose = html.indexOf("</div>", pos);
    if (nextClose === -1) return null;
    if (nextOpen !== -1 && nextOpen < nextClose) {
      const tagEnd = html.indexOf(">", nextOpen);
      if (tagEnd === -1) return null;
      depth++;
      pos = tagEnd + 1;
    } else {
      depth--;
      if (depth === 0) return { start, end: nextClose + "</div>".length };
      pos = nextClose + "</div>".length;
    }
  }
  return null;
}

function findAllRanges(html, openRegex) {
  const ranges = [];
  let from = 0;
  while (from < html.length) {
    const r = findRange(html, openRegex, from);
    if (!r) break;
    ranges.push(r);
    from = r.end;
  }
  return ranges;
}

// Ranges of the secure-link block for THIS transfer OID only (id-matched,
// depth-walked) — so find/replace/remove never touches a block for a different
// transfer (e.g. one quoted in reply/forward history). Returns [] without an OID.
export function findSecureLinkRanges(html, transferOid) {
  if (!transferOid) return [];
  return findAllRanges(html, secureLinkOpenRegex(transferOid));
}

// Every transfer OID that has a secure-link block in the body (the `id` carries
// it, tolerating the x_ prefix Outlook web may inject). Used on pane reopen to
// discover which transfer(s) the draft already references, so the pane can ask the
// server which one is still a live transfer to resume. De-duplicated, in order.
export function findSecureLinkOids(html) {
  const re = new RegExp(
    `<div\\b[^>]*\\bid\\s*=\\s*["'](?:x_)?${escapeRegExp(SECURE_LINK_ID)}-([^"']+)["']`,
    "gi",
  );
  const oids = [];
  let m;
  while ((m = re.exec(String(html))) !== null) {
    if (m[1] && !oids.includes(m[1])) oids.push(m[1]);
  }
  return oids;
}

// ---------------------------------------------------------------------------
// Visual tokens — reconciled against the LeapFILE brand palette. Teal is the
// app brand teal (#00687A), not the mockup's #0a8f86. Email HTML can't use CSS
// vars, so every value is hardcoded inline.
// ---------------------------------------------------------------------------

const FONT_STACK = "'Segoe UI',Arial,sans-serif";
const TEAL = "#00687A"; // brand teal — header label + button + trust line
const MAX_NAMES = 3; // before truncating to "name1, name2, name3, +N"

// The sent card palette (full-color brand card).
const LIVE = {
  cardBorder: "#d9e6e4",
  cardBg: "#fbfdfc",
  headerBg: "#f0f8f7",
  headerBorder: "#e3efed",
  headerText: TEAL,
  fileText: "#1a1a1a",
  fileSub: "#6b7775",
  footer: "#8a9492",
  amberBg: "#fff7ec",
  amberBorder: "#ffe2b8",
  amberText: "#9a5b00",
};

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${i === 0 || value >= 10 || value === Math.round(value) ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

/**
 * Reduce a queue (files_fragment's in-memory queue — name/size/status) to the
 * manifest the card's file area renders: a count + total size and an
 * HTML-escaped, truncated name list. Returns null when there's nothing to show
 * (the amber "no files yet" notice). Errored uploads are excluded — they won't be
 * sent.
 */
export function buildManifest(entries) {
  const live = (entries || []).filter((e) => e && e.status !== "error");
  if (!live.length) return null;
  const totalBytes = live.reduce((sum, e) => sum + (e.size || 0), 0);
  const names = live.map((e) => e.name);
  const shown = names.slice(0, MAX_NAMES).map(escapeHtml);
  const extra = names.length - shown.length;
  return {
    count: live.length,
    sizeText: formatFileSize(totalBytes),
    names: extra > 0 ? `${shown.join(", ")}, +${extra}` : shown.join(", "),
  };
}

// --- card regions --------------------------------------------------------

function headerHtml(p) {
  return (
    `<tr><td style="background:${p.headerBg};border-bottom:1px solid ${p.headerBorder};padding:6px 13px;">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">` +
    `<tr>` +
    `<td valign="middle" style="font-family:${FONT_STACK};font-size:10px;font-weight:700;color:${p.headerText};letter-spacing:.3px;">` +
    `&#128274; SECURE DOWNLOAD &middot; LEAPFILE</td>` +
    `</tr></table></td></tr>`
  );
}

function fileAreaHtml(manifest, p) {
  if (!manifest) {
    return (
      `<div style="font-family:${FONT_STACK};font-size:12px;background:${p.amberBg};` +
      `border:1px solid ${p.amberBorder};color:${p.amberText};border-radius:6px;padding:8px 10px;">` +
      `No files added yet &mdash; <b>add files in the LeapFILE panel</b></div>`
    );
  }
  const label = `${manifest.count} ${manifest.count === 1 ? "file" : "files"} &middot; ${manifest.sizeText}`;
  return (
    `<div style="font-family:${FONT_STACK};font-size:13px;color:${p.fileText};">${label}` +
    `<span style="display:block;color:${p.fileSub};font-size:12px;margin-top:2px;">${manifest.names}</span></div>`
  );
}

function buttonHtml(downloadLink) {
  // Full-width teal button with a VML roundrect fallback for Outlook/Word.
  const href = escapeHtml(downloadLink);
  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;">` +
    `<tr><td>` +
    `<!--[if mso]>` +
    `<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" ` +
    `href="${href}" style="height:42px;v-text-anchor:middle;width:100%;" arcsize="14%" ` +
    `fillcolor="${TEAL}" strokecolor="${TEAL}"><w:anchorlock/>` +
    `<center style="color:#ffffff;font-family:${FONT_STACK};font-size:14px;font-weight:bold;">Secure Download</center>` +
    `</v:roundrect>` +
    `<![endif]-->` +
    `<!--[if !mso]><!-->` +
    `<a href="${href}" target="_blank" rel="noopener" ` +
    `style="display:block;background:${TEAL};color:#ffffff;text-decoration:none;text-align:center;` +
    `font-family:${FONT_STACK};font-size:14px;font-weight:700;line-height:1;padding:11px;border-radius:6px;">Secure Download</a>` +
    `<!--<![endif]-->` +
    `</td></tr></table>`
  );
}

function footerHtml(availableUntil, p) {
  const tail = availableUntil ? ` &middot; available until ${escapeHtml(availableUntil)}` : "";
  return (
    `<div style="font-family:${FONT_STACK};font-size:10px;color:${p.footer};text-align:center;">` +
    `Encrypted &amp; delivered by LeapFILE${tail}</div>`
  );
}

function spacerHtml(height) {
  return `<div style="font-size:0;line-height:${height}px;height:${height}px;">&nbsp;</div>`;
}

/**
 * The live secure-download block: full-color brand card, real teal "Secure
 * Download" button linking to the transfer, the final manifest, and the
 * availability date in the footer. Carries a per-transfer
 * ``${SECURE_LINK_ID}-<transferOid>`` wrapper so the pane can find and replace
 * exactly this block later without touching blocks for other transfers.
 */
export function buildSecureLinkBlock(downloadLink, manifest = null, availableUntil = "", transferOid = "") {
  const p = LIVE;
  return (
    `<div id="${SECURE_LINK_ID}-${transferOid}" style="margin:20px 0;font-family:${FONT_STACK};">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-collapse:separate;max-width:560px;">` +
    `<tr><td style="border:1px solid ${p.cardBorder};border-radius:8px;background:${p.cardBg};">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;">` +
    headerHtml(p) +
    `<tr><td style="padding:13px 15px;">` +
    fileAreaHtml(manifest, p) +
    spacerHtml(11) +
    buttonHtml(downloadLink) +
    spacerHtml(7) +
    footerHtml(availableUntil, p) +
    `</td></tr>` +
    `</table></td></tr></table></div>`
  );
}

// ===========================================================================
// Body ops — the Office.js side. Everything above is pure (given data → strings);
// everything below reads/writes the live compose body, scoped to a single transfer
// OID so it never touches a block for another transfer (e.g. one quoted in
// reply/forward history). This is the only place the pane writes to the body.
// ===========================================================================

// Put the live secure-link block for `oid` into the body. Scoped to THIS transfer:
// if a block for the same OID is already present (re-attach), replace it in place
// (dropping any duplicate of the same OID); otherwise insert at the caret with a
// whole-body prepend fallback. Blocks for other transfers are never matched.
export async function insertSecureLink(item, html, oid) {
  const body = await getBodyHtml(item);
  const ranges = findSecureLinkRanges(body, oid);
  if (ranges.length === 0) {
    try {
      await setSelectedHtml(item, html);
      // OWA can report success without inserting when the body never had focus
      // (office-js#6811) — verify the card landed and prepend if it didn't.
      if (findSecureLinkRanges(await getBodyHtml(item), oid).length === 0) {
        await setBodyHtml(item, html + body);
      }
    } catch {
      await setBodyHtml(item, html + body);
    }
    return;
  }
  // Gap-copy: keep everything between our same-OID blocks, swap the first for the
  // new block, drop any extras.
  let out = "";
  let cursor = 0;
  ranges.forEach((r, i) => {
    out += body.slice(cursor, r.start);
    if (i === 0) out += html;
    cursor = r.end;
  });
  out += body.slice(cursor);
  await setBodyHtml(item, out);
}

// Rebuild this session's secure-link card from the currently-attached files and
// swap it into the body in place, so the card's file summary (count · size ·
// names) tracks files added/removed after the first attach. The download button
// is unchanged — it's keyed by the transfer OID — only the manifest is refreshed.
// Always refreshes the in-memory block (so a later re-attach is current), but only
// touches the body when the link is actually there: if the user deleted it, we
// don't force it back (they can restore it with Re-attach).
export async function refreshSecureLink() {
  const item = Office.context.mailbox && Office.context.mailbox.item;
  if (!item || !state.lastAttached) return;
  const attached = state.queue.filter((e) => e.status === "attached");
  const block = buildSecureLinkBlock(
    state.lastAttached.downloadLink,
    buildManifest(attached),
    state.lastAttached.availableUntil,
    state.lastAttached.oid,
  );
  state.lastAttached.block = block;
  try {
    const body = await getBodyHtml(item);
    if (findSecureLinkRanges(body, state.lastAttached.oid).length === 0) return; // link removed — leave it out
    await insertSecureLink(item, block, state.lastAttached.oid);
  } catch (err) {
    console.warn("[Files] refresh secure link failed:", err);
  }
}

// Remove the secure-link card(s) for the given transfer OID(s) from the body —
// scoped by OID, so a card for another transfer (e.g. one quoted in reply/forward
// history) is never touched. Body only: it does NOT delete the transfer
// server-side. Accepts one OID or a list; ranges are collected against a single
// body read and spliced out back-to-front so indices stay valid. Best-effort.
export async function removeSecureLinksFromBody(item, oids) {
  const list = (Array.isArray(oids) ? oids : [oids]).filter(Boolean);
  if (!list.length) return;
  try {
    let body = await getBodyHtml(item);
    const ranges = list.flatMap((oid) => findSecureLinkRanges(body, oid)).sort((a, b) => a.start - b.start);
    if (!ranges.length) return;
    for (let i = ranges.length - 1; i >= 0; i--) {
      body = body.slice(0, ranges[i].start) + body.slice(ranges[i].end);
    }
    await setBodyHtml(item, body);
  } catch (err) {
    console.warn("[Files] remove secure links from body failed:", err);
  }
}

// Discard the transfer this pane session owns: remove ITS block from the body (by
// OID — never a block for another transfer, e.g. one quoted in reply/forward
// history) and delete the transfer. Used on Cancel and when starting a new
// transfer. Deletes the transfer even if the user already removed the visible
// link. No-op without an owned OID. Best-effort.
export async function discardOwnedTransfer(item, oid) {
  if (!oid) return;
  await removeSecureLinksFromBody(item, oid);
  // We created it this session — delete regardless of the body. Must be awaited:
  // Cancel closes the pane right after this returns, and closing the webview
  // aborts any in-flight fetch (nginx logs 499), leaving the transfer alive.
  // Still best-effort — a server failure logs and lets the transfer expire
  // rather than trapping the user in the pane.
  await deleteTransfer(oid).catch((err) => console.warn("[Files] delete transfer failed:", err));
}
